import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Autocomplete,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SaveIcon from "@mui/icons-material/Save";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import UndoIcon from "@mui/icons-material/Undo";
import EventIcon from "@mui/icons-material/Event";
import { useDispatch, useSelector } from "react-redux";
import HeaderBannerPage from "../components/HeaderBannerPage";
import { fetchPmIwoList } from "../actions/ProductionMonitorAction";
import {
  fetchErUnits,
  fetchErFabTypes,
  fetchErWeights,
  fetchErUnitFabrications,
  saveErTargetDates,
} from "../actions/ErectionMonitorAction";

/* -------------------- Erection -> Insert Target Dates --------------------
   1. IWO   (PreFabMonitor/GetIwoList)
   2. Unit  (ErecMonitor/GetUnits)
   3. Table: fabrication types (GetFabTypes, SeqNo order)
             + contribution % of the unit (GetWeights?unitId=)
             + target date (saved ones from GetUnitFabrications?iwoNo=&unitId=)
   Save: first time InsertTargetDates, after that UpdateTargetDates (changed rows only)
--------------------------------------------------------------------------- */

const NAVY = "#142c54";
const BLUE = "#154c79";

const toYmd = (v) => String(v || "").slice(0, 10);

// typed digits -> yyyy-MM-dd
const formatTypedDate = (text) => {
  const digits = String(text || "").replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 4) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 4)}-${digits.slice(4)}`;
  return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6)}`;
};

const isValidYmd = (v) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v || "")) return false;
  const [y, m, d] = v.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
};

const pct = (n) => `${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}%`;

const cardSx = {
  p: { xs: 1.5, sm: 2.5 },
  borderRadius: "16px",
  border: "1px solid #d6dce5",
  backgroundColor: "#fff",
  boxShadow: "0px 4px 12px rgba(0,0,0,0.06)",
};
const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: "12px", backgroundColor: "#fff" } };

/* ---- yyyy-mm-dd box with a calendar button ---- */
const DateBox = ({ value, onChange, label, error, changed, disabled }) => {
  const pickerRef = useRef(null);
  const openPicker = () => {
    const el = pickerRef.current;
    if (!el) return;
    if (typeof el.showPicker === "function") el.showPicker();
    else el.click();
  };
  return (
    <Box sx={{ position: "relative" }}>
      <TextField
        size="small"
        fullWidth
        value={value}
        placeholder="yyyy-mm-dd"
        disabled={disabled}
        error={error}
        onChange={(e) => onChange(formatTypedDate(e.target.value))}
        inputProps={{ inputMode: "numeric", maxLength: 10, "aria-label": label }}
        InputProps={{
          endAdornment: (
            <IconButton size="small" edge="end" onClick={openPicker} disabled={disabled} aria-label={`Pick ${label}`}>
              <CalendarMonthIcon sx={{ fontSize: 18, color: BLUE }} />
            </IconButton>
          ),
        }}
        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", backgroundColor: changed ? "#fff8e6" : "#fff" } }}
      />
      {/* hidden native picker - only opens the calendar, value stays yyyy-mm-dd */}
      <input
        ref={pickerRef}
        type="date"
        tabIndex={-1}
        aria-hidden="true"
        value={isValidYmd(value) ? value : ""}
        onChange={(e) => e.target.value && onChange(e.target.value)}
        style={{ position: "absolute", right: 8, bottom: 0, width: 1, height: 1, opacity: 0, border: 0, padding: 0, pointerEvents: "none" }}
      />
    </Box>
  );
};

/* -------------------- Page -------------------- */
const InsertTargetDates = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const userId = localStorage.getItem("employeeId") || "";

  const { iwoList, iwoListLoading, iwoListError } = useSelector((state) => state.productionMonitor);
  const {
    units,
    unitsLoading,
    unitsError,
    fabTypes,
    fabTypesLoading,
    fabTypesError,
    weights,
    weightsLoading,
    weightsError,
    unitFabrications,
    unitFabricationsLoading,
    unitFabricationsError,
    targetSaving,
  } = useSelector((state) => state.erectionMonitor);

  const [iwo, setIwo] = useState(null);
  const [unit, setUnit] = useState(null);
  const [dates, setDates] = useState({}); // typeId -> typed date
  const [showErrors, setShowErrors] = useState(false);
  const [snack, setSnack] = useState({ open: false, severity: "success", message: "" });

  useEffect(() => {
    dispatch(fetchPmIwoList());
    dispatch(fetchErUnits());
    dispatch(fetchErFabTypes());
  }, [dispatch]);

  const iwoNo = iwo ? String(iwo.IwoNo) : "";
  const unitId = unit ? unit.UnitId : null;

  // contribution of the unit + saved target dates of IWO + unit
  useEffect(() => {
    if (unitId) dispatch(fetchErWeights(unitId));
  }, [dispatch, unitId]);
  useEffect(() => {
    setDates({});
    setShowErrors(false);
    if (iwoNo && unitId) dispatch(fetchErUnitFabrications(iwoNo, unitId));
  }, [dispatch, iwoNo, unitId]);

  const activeUnits = useMemo(() => (units || []).filter((u) => u.IsActive !== false), [units]);

  // saved row per type (only when the answer is for this IWO + unit)
  const savedForThis =
    unitFabrications && String(unitFabrications.IwoNo) === iwoNo && String(unitFabrications.UnitId) === String(unitId)
      ? unitFabrications
      : null;
  const isSaved = !!savedForThis?.IsSaved;
  const componentId = savedForThis?.ComponentId ?? null; 

  // table rows: fabrication types in SeqNo order + contribution + saved target date
  const rows = useMemo(() => {
    const savedMap = new Map((savedForThis?.Fabrications || []).map((f) => [f.TypeId, f]));
    const weightMap = new Map((weights || []).map((w) => [w.TypeId, w]));
    return [...(fabTypes || [])]
      .filter((t) => t.IsActive !== false)
      .sort((a, b) => Number(a.SeqNo) - Number(b.SeqNo))
      .map((t) => {
        const saved = savedMap.get(t.TypeId) || null;
        const w = weightMap.get(t.TypeId);
        const savedDate = toYmd(saved?.TargetDate);
        return {
          typeId: t.TypeId,
          seqNo: t.SeqNo,
          typeName: t.TypeName,
          contribution: w ? Number(w.ContributePercentage) : saved ? Number(saved.ContributePercentage) : null,
          progressId: saved?.ProgressId || null,
          savedDate,
          completed: saved?.CompletionStatus === true,
          physical: saved ? Number(saved.PhysicalCompletion) || 0 : 0,
          setBy: saved?.TargetCreatedBy || "",
          setOn: saved?.TargetCreatedDate || "",
        };
      });
  }, [fabTypes, weights, savedForThis]);

  const totalContribution = rows.reduce((n, r) => n + (r.contribution || 0), 0);
  const valueOf = (r) => (dates[r.typeId] !== undefined ? dates[r.typeId] : r.savedDate);
  const isChanged = (r) => dates[r.typeId] !== undefined && dates[r.typeId] !== r.savedDate;
  const rowError = (r) => {
    const v = valueOf(r);
    if (v && !isValidYmd(v)) return "Invalid date";
    return "";
  };

  // what will be sent
  const toSend = rows
    .filter((r) => (isSaved ? isChanged(r) : !!valueOf(r)))
    .filter((r) => isValidYmd(valueOf(r)))
    .map((r) => ({ progressId: r.progressId, typeId: r.typeId, targetDate: valueOf(r) }));
  const hasErrors = rows.some((r) => !!rowError(r));
  const filledCount = rows.filter((r) => isValidYmd(valueOf(r))).length;

  const handleSave = async () => {
    setShowErrors(true);
    if (hasErrors) {
      setSnack({ open: true, severity: "error", message: "Fix the dates marked in red (yyyy-mm-dd)" });
      return;
    }
    if (toSend.length === 0) return;
    const result = await dispatch(saveErTargetDates({ isSaved, componentId, iwoNo, unitId, fabrications: toSend, userId }));
    if (result.success) {
      setSnack({
        open: true,
        severity: "success",
        message: `${toSend.length} target date(s) ${isSaved ? "updated" : "saved"} for IWO ${iwoNo} · ${unit.UnitName}`,
      });
      setDates({});
      setShowErrors(false);
      dispatch(fetchErUnitFabrications(iwoNo, unitId)); // reload saved dates
    } else {
      setSnack({ open: true, severity: "error", message: result.message });
    }
  };

  const loadingTable = fabTypesLoading || weightsLoading || unitFabricationsLoading;
  const tableError = fabTypesError || weightsError || unitFabricationsError;

  /* ---- table / cards ---- */
  const th = {
    position: "sticky",
    top: 0,
    zIndex: 2,
    backgroundColor: BLUE,
    color: "#fff",
    fontWeight: 700,
    fontSize: "13px",
    textAlign: "left",
    padding: "10px 10px",
    whiteSpace: "nowrap",
    fontFamily: "inherit",
  };
  const td = { padding: "8px 10px", borderBottom: "1px solid #eef1f5", fontSize: "13px", verticalAlign: "middle", fontFamily: "inherit" };

  const statusChip = (r) =>
    r.completed ? (
      <Chip size="small" label="Completed" color="success" sx={{ height: 20, fontSize: "11px" }} />
    ) : r.savedDate ? (
      <Tooltip title={r.setBy ? `Set by ${r.setBy}${r.setOn ? ` · ${r.setOn}` : ""}` : ""}>
        <Chip size="small" label="Saved" sx={{ height: 20, fontSize: "11px", backgroundColor: "#e8f0fa", color: "#0E3C66", fontWeight: 600 }} />
      </Tooltip>
    ) : (
      <Chip size="small" label="Not set" sx={{ height: 20, fontSize: "11px", color: "#9aa3b2" }} />
    );

  const dateCell = (r) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <Box sx={{ flex: 1, minWidth: 150 }}>
        <DateBox
          label={`Target date for ${r.typeName}`}
          value={valueOf(r)}
          onChange={(v) => setDates((prev) => ({ ...prev, [r.typeId]: v }))}
          error={showErrors && !!rowError(r)}
          changed={isChanged(r)}
          disabled={targetSaving}
        />
      </Box>
      {isChanged(r) && r.savedDate && (
        <Tooltip title={`Undo (was ${r.savedDate})`}>
          <IconButton
            size="small"
            onClick={() =>
              setDates((prev) => {
                const n = { ...prev };
                delete n[r.typeId];
                return n;
              })
            }
          >
            <UndoIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );

  return (
    <Box sx={{ width: "100%", minHeight: "100vh", display: "flex", flexDirection: "column", backgroundColor: "#f5f7fb" }}>
      <HeaderBannerPage />

      <Box
        sx={{
          maxWidth: "1000px",
          width: "100%",
          mx: "auto",
          px: { xs: 1.5, sm: 2, md: 4 },
          py: { xs: 2, md: 3 },
          boxSizing: "border-box",
          fontFamily: "'Poppins', sans-serif",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <EventIcon sx={{ color: BLUE }} />
          <Typography sx={{ fontSize: { xs: "19px", md: "22px" }, fontWeight: 700, color: "#0E3C66", letterSpacing: "0.5px" }}>
            Insert Target Dates
          </Typography>
          <Chip size="small" label="Erection" sx={{ ml: 0.5, backgroundColor: "#e8f0fa", color: "#0E3C66", fontWeight: 600 }} />
        </Box>

        {/* IWO + Unit */}
        <Paper elevation={0} sx={{ ...cardSx, mb: 2 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 1.6fr) minmax(0, 1fr)" }, gap: 1.5 }}>
            <Autocomplete
              options={iwoList || []}
              value={iwo}
              onChange={(_, v) => {
                setIwo(v);
                setUnit(null);
              }}
              loading={iwoListLoading}
              autoHighlight
              getOptionLabel={(o) => (o ? `${o.IwoNo} - ${o.EngSpec || ""}`.trim() : "")}
              isOptionEqualToValue={(o, v) => String(o.IwoNo) === String(v.IwoNo)}
              filterOptions={(opts, { inputValue }) => {
                const q = inputValue.trim().toLowerCase();
                if (!q) return opts;
                return opts.filter((o) =>
                  [o.IwoNo, o.EngSpec, o.AdditionalDescription].some((v) => String(v || "").toLowerCase().includes(q))
                );
              }}
              renderOption={(props, o) => (
                <Box component="li" {...props} key={o.IwoNo} sx={{ display: "block !important", py: "8px !important" }}>
                  <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#0E3C66" }}>{o.IwoNo}</Typography>
                  {o.EngSpec && (
                    <Typography sx={{ fontSize: "12px", color: "#555", whiteSpace: "normal", wordBreak: "break-word" }}>{o.EngSpec}</Typography>
                  )}
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="IWO No - Eng Spec"
                  placeholder="Search IWO no or eng spec"
                  error={!!iwoListError}
                  helperText={iwoListError || ""}
                  sx={fieldSx}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {iwoListLoading ? <CircularProgress size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            <Autocomplete
              options={activeUnits}
              value={unit}
              onChange={(_, v) => setUnit(v)}
              disabled={!iwo}
              loading={unitsLoading}
              autoHighlight
              getOptionLabel={(u) => (u ? u.UnitName || "" : "")}
              isOptionEqualToValue={(o, v) => o.UnitId === v.UnitId}
              noOptionsText="No units"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Unit"
                  placeholder={iwo ? "Select unit" : "Select an IWO first"}
                  error={!!unitsError}
                  helperText={unitsError || ""}
                  sx={fieldSx}
                />
              )}
            />
          </Box>
        </Paper>

        {!iwo || !unit ? (
          <Paper elevation={0} sx={cardSx}>
            <Typography sx={{ fontSize: "13px", color: "#9aa3b2", py: 5, textAlign: "center" }}>
              {!iwo ? "Select an IWO to start" : "Select a unit to see its fabrication steps"}
            </Typography>
          </Paper>
        ) : (
          <Paper elevation={0} sx={{ ...cardSx, p: { xs: 1.5, sm: 2 } }}>
            {/* summary */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
              <Typography sx={{ fontWeight: 700, color: NAVY, fontSize: "15px" }}>{unit.UnitName}</Typography>
              <Chip size="small" label={`IWO ${iwoNo}`} sx={{ backgroundColor: NAVY, color: "#fff", fontWeight: 600 }} />
              <Chip
                size="small"
                label={isSaved ? "Target dates saved" : "Not saved yet"}
                sx={{ fontWeight: 600, backgroundColor: isSaved ? "#eaf7ee" : "#fff6e5", color: isSaved ? "#2e7d32" : "#b45309" }}
              />
              <Typography sx={{ ml: "auto", fontSize: "12px", color: "#6b7280" }}>
                {filledCount}/{rows.length} dates · contribution {pct(totalContribution)}
              </Typography>
            </Box>

            {tableError && <Alert severity="error" sx={{ mb: 1.5, borderRadius: "12px" }}>{tableError}</Alert>}

            {loadingTable ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1, py: 5 }}>
                <CircularProgress size={22} />
                <Typography sx={{ fontSize: "13px", color: "#6b7280" }}>Loading…</Typography>
              </Box>
            ) : rows.length === 0 ? (
              <Typography sx={{ fontSize: "13px", color: "#9aa3b2", py: 4, textAlign: "center" }}>No fabrication types found</Typography>
            ) : isMobile ? (
              /* ---- phones: one card per step ---- */
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {rows.map((r) => (
                  <Box key={r.typeId} sx={{ p: 1.25, borderRadius: "12px", border: "1px solid #e3e8ef", backgroundColor: isChanged(r) ? "#fffcf2" : "#fafbfd" }}>
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1 }}>
                      <Box
                        sx={{ minWidth: 24, height: 24, borderRadius: "8px", backgroundColor: "#e8f0fa", color: "#0E3C66", fontSize: "12px", fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}
                      >
                        {r.seqNo}
                      </Box>
                      <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#0E3C66", flex: 1 }}>{r.typeName}</Typography>
                      <Typography sx={{ fontSize: "13px", fontWeight: 700, color: NAVY, whiteSpace: "nowrap" }}>
                        {r.contribution === null ? "—" : pct(r.contribution)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ flex: 1 }}>{dateCell(r)}</Box>
                      {statusChip(r)}
                    </Box>
                    {showErrors && rowError(r) && <Typography sx={{ fontSize: "11px", color: "#c62828", mt: 0.5 }}>{rowError(r)}</Typography>}
                  </Box>
                ))}
              </Box>
            ) : (
              /* ---- desktop: table ---- */
              <Box sx={{ overflow: "auto", borderRadius: "12px", border: "1px solid #eef1f5" }}>
                <Box component="table" sx={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, fontFamily: (t) => t.typography.fontFamily }}>
                  <Box component="thead">
                    <Box component="tr">
                      <Box component="th" sx={{ ...th, width: 50, textAlign: "center" }}>#</Box>
                      <Box component="th" sx={th}>Unit Fabrication</Box>
                      <Box component="th" sx={{ ...th, width: 120, textAlign: "right" }}>Contribution</Box>
                      <Box component="th" sx={{ ...th, width: 230 }}>Target Date</Box>
                      <Box component="th" sx={{ ...th, width: 110, textAlign: "center" }}>Status</Box>
                    </Box>
                  </Box>
                  <Box component="tbody">
                    {rows.map((r, i) => (
                      <Box
                        component="tr"
                        key={r.typeId}
                        sx={{ backgroundColor: isChanged(r) ? "#fffcf2" : i % 2 ? "#fbfcfe" : "#fff" }}
                      >
                        <Box component="td" sx={{ ...td, textAlign: "center", color: "#6b7280", fontWeight: 600 }}>{r.seqNo}</Box>
                        <Box component="td" sx={{ ...td, fontWeight: 600, color: "#0E3C66" }}>{r.typeName}</Box>
                        <Box component="td" sx={{ ...td, textAlign: "right", fontWeight: 700, color: NAVY, fontVariantNumeric: "tabular-nums" }}>
                          {r.contribution === null ? "—" : pct(r.contribution)}
                        </Box>
                        <Box component="td" sx={td}>
                          {dateCell(r)}
                          {showErrors && rowError(r) && <Typography sx={{ fontSize: "11px", color: "#c62828", mt: 0.25 }}>{rowError(r)}</Typography>}
                        </Box>
                        <Box component="td" sx={{ ...td, textAlign: "center" }}>{statusChip(r)}</Box>
                      </Box>
                    ))}
                    <Box component="tr">
                      <Box component="td" sx={{ ...td, borderBottom: 0 }} />
                      <Box component="td" sx={{ ...td, borderBottom: 0, fontWeight: 700, color: "#6b7280" }}>Total</Box>
                      <Box
                        component="td"
                        sx={{ ...td, borderBottom: 0, textAlign: "right", fontWeight: 800, color: Math.round(totalContribution) === 100 ? "#2e7d32" : "#d97706" }}
                      >
                        {pct(totalContribution)}
                      </Box>
                      <Box component="td" sx={{ ...td, borderBottom: 0 }} />
                      <Box component="td" sx={{ ...td, borderBottom: 0 }} />
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}

            {/* save */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mt: 2,
                position: { xs: "sticky", md: "static" },
                bottom: 0,
                backgroundColor: "#fff",
                py: { xs: 1, md: 0 },
              }}
            >
              <Typography sx={{ fontSize: "12px", color: "#6b7280", mr: "auto" }}>
                {toSend.length > 0 ? `${toSend.length} date(s) to ${isSaved ? "update" : "save"}` : isSaved ? "No changes" : "Enter target dates"}
              </Typography>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={targetSaving || loadingTable || toSend.length === 0}
                startIcon={targetSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: "10px",
                  px: 3,
                  backgroundColor: "#2e7d32",
                  "&:hover": { backgroundColor: "#1b5e20" },
                }}
              >
                {isSaved ? "Update Target Dates" : "Save Target Dates"}
              </Button>
            </Box>
          </Paper>
        )}
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InsertTargetDates;